import { LiturgicalYear } from './calendar.ts';
import {
  addDayOffset,
  convertLiturgicalDateToDateString,
  DateString,
  DayOfWeek,
  daysBetween,
  getDayOfWeek,
  getYear,
  lastSundayDate,
  thisSundayDate,
  validateDateString,
} from './date_string.ts';

const maxOrdinarySundayIndex = 29;
const ordinarySundayName = 'Ordinary Sunday';

export function isOrdinarySundayName(sundayName: string): boolean {
  return sundayName.startsWith(ordinarySundayName);
}

function getSundayNumberFromIndex(index: number, { useRomanNumerals }: { useRomanNumerals?: boolean }): string {
  const romanNumerals = [
    'I',
    'II',
    'III',
    'IV',
    'V',
    'VI',
    'VII',
    'VIII',
    'IX',
    'X',
    'XI',
    'XII',
    'XIII',
    'XIV',
    'XV',
    'XVI',
    'XVII',
    'XVIII',
    'XIX',
    'XX',
    'XXI',
    'XXII',
    'XXIII',
    'XXIV',
    'XXV',
    'XXVI',
    'XXVII',
    'XXVIII',
    'XXIX',
    'XXX',
  ];
  if (romanNumerals.length < maxOrdinarySundayIndex + 1) {
    throw new Error('Roman numerals array length is too short');
  }
  if (index < 0 || index > maxOrdinarySundayIndex) {
    throw new Error(`Sunday index out of bounds ${index}`);
  }
  return useRomanNumerals ? romanNumerals[index] : String(index + 1);
}

export function getNameForSunday(
  date: DateString,
  { useRomanNumerals, useNameAsType }: { useRomanNumerals?: boolean; useNameAsType?: boolean } = {},
): string {
  validateDateString(date);

  if (getDayOfWeek(date) !== DayOfWeek.Sunday) {
    throw new Error(`Date: ${date} is not a sunday`);
  }

  const nextYear = LiturgicalYear.get(getYear(date) + 1);
  const thisYear = LiturgicalYear.get(getYear(date));

  const thisChristmas = convertLiturgicalDateToDateString(nextYear.christmasDay);
  const lastChristmas = convertLiturgicalDateToDateString(thisYear.christmasDay);
  const epiphany = convertLiturgicalDateToDateString(thisYear.epiphany);
  const reformationDay = convertLiturgicalDateToDateString(thisYear.reformationDay);
  const allSaintsDay = convertLiturgicalDateToDateString(thisYear.allSaintsDay);
  const easter = convertLiturgicalDateToDateString(thisYear.easter);

  const weeksBetweenEasterAndAscensionSunday = 6;
  const weeksBetweenEasterAndPentecost = weeksBetweenEasterAndAscensionSunday + 1;
  const weeksBetweenEasterAndOrdinary = weeksBetweenEasterAndPentecost + 1;
  const weeksOfLent = 6;
  const weeksOfEaster = 6;
  const weeksOfAdvent = 4;
  const maxWeeksOfEpiphany = 9;

  if (getDayOfWeek(lastChristmas) !== DayOfWeek.Monday && getDayOfWeek(lastChristmas) !== DayOfWeek.Tuesday) {
    if (date === addDayOffset(thisSundayDate(lastChristmas), 7) && date !== epiphany) {
      return `Christmas ${getSundayNumberFromIndex(1, { useRomanNumerals })}`;
    }
  }

  if (!useNameAsType && date === easter) {
    return 'Easter';
  }

  // Lent takes precedence over Epiphany & Easter
  const weeksUntilEasterSunday = daysBetween(date, easter) / 7;
  if (weeksUntilEasterSunday >= 0 && weeksUntilEasterSunday <= weeksOfLent) {
    return `Lent ${getSundayNumberFromIndex(weeksOfLent - weeksUntilEasterSunday, { useRomanNumerals })}`;
  }

  const weeksSinceEasterSunday = daysBetween(easter, date) / 7;
  if (weeksSinceEasterSunday >= 0 && weeksSinceEasterSunday < weeksOfEaster) {
    return `Easter ${getSundayNumberFromIndex(weeksSinceEasterSunday, { useRomanNumerals })}`;
  }

  if (date === epiphany) {
    return 'Epiphany';
  }
  const weeksSinceEpiphanySunday = daysBetween(thisSundayDate(epiphany), date) / 7;
  if (weeksSinceEpiphanySunday >= 0 && weeksSinceEpiphanySunday < maxWeeksOfEpiphany) {
    return `Epiphany ${getSundayNumberFromIndex(weeksSinceEpiphanySunday, { useRomanNumerals })}`;
  }

  if (weeksSinceEasterSunday === weeksBetweenEasterAndAscensionSunday) {
    return 'Ascension Sunday';
  }

  if (weeksSinceEasterSunday === weeksBetweenEasterAndPentecost) {
    return 'Pentecost';
  }

  if (!useNameAsType && date === reformationDay) {
    return 'Reformation Day';
  }
  if (
    date === reformationDay ||
    (getDayOfWeek(reformationDay) !== DayOfWeek.Sunday && date === lastSundayDate(allSaintsDay))
  ) {
    return 'Reformation Sunday';
  }

  if (!useNameAsType && date === allSaintsDay) {
    return 'All Saints Day';
  }
  if (date === thisSundayDate(allSaintsDay)) {
    return 'All Saints Sunday';
  }

  if (!useNameAsType && date === thisChristmas) {
    return 'Christmas';
  }
  if (date === thisSundayDate(thisChristmas)) {
    return `Christmas ${getSundayNumberFromIndex(0, { useRomanNumerals })}`;
  }

  const weeksBeforeChristmas = daysBetween(date, thisSundayDate(thisChristmas)) / 7;
  if (weeksBeforeChristmas >= 0 && weeksBeforeChristmas <= weeksOfAdvent) {
    return `Advent ${getSundayNumberFromIndex(weeksOfAdvent - weeksBeforeChristmas, { useRomanNumerals })}`;
  }

  const weeksAfterEaster = daysBetween(easter, date) / 7;
  if (weeksAfterEaster < 0) {
    throw new Error(`Expected date ${date} to be after easter`);
  }
  return `${ordinarySundayName} ${getSundayNumberFromIndex(weeksAfterEaster - weeksBetweenEasterAndOrdinary, { useRomanNumerals })}`;
}

export function getSeasonalCategoryForSundayName(sundayName: string): string {
  let prefix: string;
  if (isOrdinarySundayName(sundayName)) {
    prefix = 'Seasonal';
  } else {
    const nameParts = sundayName.split(' ');
    prefix = nameParts.length === 1 ? sundayName : nameParts.slice(0, nameParts.length - 1).join(' ');
  }
  return `${prefix} Song`;
}
